import { getCampaignParams } from "~/lib/campaignParams";

const GA_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;

/**
 * Get campaign parameters from localStorage and format them for gtag
 * @returns Formatted campaign parameters object
 */
const getEnrichedCampaignParams = (): Record<string, string> => {
  const params = getCampaignParams();
  if (!params) return {};

  // Filter out null/undefined values and return only defined params
  const enrichedParams: Record<string, string> = {};

  if (params.utm_source) enrichedParams.utm_source = params.utm_source;
  if (params.utm_medium) enrichedParams.utm_medium = params.utm_medium;
  if (params.utm_campaign) enrichedParams.utm_campaign = params.utm_campaign;
  if (params.utm_content) enrichedParams.utm_content = params.utm_content;
  if (params.utm_term) enrichedParams.utm_term = params.utm_term;
  if (params.gclid) enrichedParams.gclid = params.gclid;
  if (params.msclkid) enrichedParams.msclkid = params.msclkid;

  return enrichedParams;
};

/**
 * Check if gtag is enabled and available
 */
export const isGtagEnabled = (): boolean => {
  return (
    !!GA_ID &&
    typeof window !== "undefined" &&
    typeof window.dataLayer !== "undefined"
  );
};

/**
 * Track a custom event with gtag
 * @param eventName - Name of the event to track
 * @param params - Additional parameters for the event
 */
export const trackEvent = (
  eventName: string,
  params?: Record<string, any>
): void => {
  if (!isGtagEnabled()) {
    console.debug(`Gtag not enabled, skipping event: ${eventName}`);
    return;
  }

  try {
    const campaignParams = getEnrichedCampaignParams();
    const eventParams = {
      ...campaignParams,
      ...params,
    };

    window.dataLayer.push({
      event: eventName,
      ...eventParams,
    });

    console.debug(`Gtag event tracked: ${eventName}`, eventParams);
  } catch (error) {
    console.error(`Failed to track gtag event: ${eventName}`, error);
  }
};

/**
 * Track a page view with gtag
 * @param path - The page path to track
 */
export const trackPageView = (path: string): void => {
  if (!isGtagEnabled()) {
    console.debug(`Gtag not enabled, skipping page view: ${path}`);
    return;
  }

  try {
    window.dataLayer.push({
      event: "page_view",
      page_path: path,
    });
    console.debug(`Gtag page view tracked: ${path}`);
  } catch (error) {
    console.error(`Failed to track gtag page view: ${path}`, error);
  }
};

/**
 * Identify a user with gtag (for User ID tracking)
 * @param userId - The user's unique identifier
 */
export const identifyUser = (userId: string): void => {
  if (!isGtagEnabled()) {
    console.debug(`Gtag not enabled, skipping user identification`);
    return;
  }

  try {
    window.dataLayer.push({
      event: "config",
      user_id: userId,
    });
    console.debug(`Gtag user identified: ${userId}`);
  } catch (error) {
    console.error(`Failed to identify gtag user: ${userId}`, error);
  }
};
